import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import * as apicall from '../../components/Apicall';
import Loader from '../../components/Loader';

const CHANNELS = ['SMS', 'WHATSAPP', 'PUSH'];
const TYPES = ['PROMOTIONAL', 'TRANSACTIONAL', 'OPERATIONAL'];
const PRIORITIES = ['NORMAL', 'HIGH'];
const TIMEZONES = ['Asia/Dubai', 'Asia/Kolkata', 'UTC'];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

const SMS_WARN_LIMIT = 160;
const PUSH_TITLE_LIMIT = 65;
const PUSH_BODY_LIMIT = 240;

const MarketingCampaignForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const campaignId = Number(id) || 0;
  const navState = location.state || {};

  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('save'); // save | send | schedule
  const [csvFile, setCsvFile] = useState(null); // { name, base64 }
  const [countries, setCountries] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: '', channel: 'SMS', campaign_type: 'PROMOTIONAL', country: 1, priority: 'NORMAL',
    title: '', body: '', cta_label: '', cta_link: '',
    whatsapp_template: '', whatsapp_lang: 'en',
    audience_type: 'ALL_CUSTOMERS', send_mode: 'NOW',
    scheduled_date: '', scheduled_time: '', timezone: 'Asia/Dubai',
    is_live: false, test_recipients: '',
    retry_enabled: false, max_retry_count: 0,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get('country');
        setCountries(res.data);
      } catch (e) {
        toast.error(e.message?.replace('Error:', '') || 'An error occurred while fetching countries');
      }
    })();
  }, []);

  useEffect(() => {
    if (campaignId > 0) {
      (async () => {
        try {
          const res = await apicall.get(`campaign/${campaignId}`);
          if (res.success) {
            const c = res.data;
            setInitialValues({
              name: c.name || '', channel: c.channel || 'SMS',
              campaign_type: c.campaign_type || 'PROMOTIONAL', country: c.country || 1, priority: c.priority || 'NORMAL',
              title: c.title || '', body: c.body || '', cta_label: c.cta_label || '', cta_link: c.cta_link || '',
              whatsapp_template: c.whatsapp_template || '', whatsapp_lang: c.whatsapp_lang || 'en',
              audience_type: c.audience_type || 'ALL_CUSTOMERS', send_mode: c.send_mode || 'NOW',
              scheduled_date: c.scheduled_at ? new Date(c.scheduled_at).toISOString().split('T')[0] : '',
              scheduled_time: c.scheduled_at ? new Date(c.scheduled_at).toISOString().split('T')[1].slice(0, 5) : '',
              timezone: c.timezone || 'Asia/Dubai',
              is_live: !!c.is_live, test_recipients: c.test_recipients || '',
              retry_enabled: !!c.retry_enabled, max_retry_count: c.max_retry_count || 0,
            });
          }
        } catch (e) {
          toast.error(e.message.replace('Error:', ''));
        }
      })();
    }
  }, [campaignId]);

  const validationSchema = Yup.object({
    name: Yup.string().required('Campaign name is required').max(200),
    channel: Yup.string().oneOf(CHANNELS).required(),
    campaign_type: Yup.string().oneOf(TYPES).required(),
    country: Yup.number().required('Country is required'),
    body: Yup.string().required('Message body is required'),
    title: Yup.string().when('channel', {
      is: 'PUSH',
      then: (s) => s.required('Title is required for Push campaigns'),
    }),
    whatsapp_template: Yup.string().when('channel', {
      is: 'WHATSAPP',
      then: (s) => s.required('WhatsApp template name is required'),
    }),
    test_recipients: Yup.string().when('is_live', {
      is: false,
      then: (s) => s.required('Add at least one test recipient (or enable Live mode)'),
    }),
  });

  const buildPayload = (values) => {
    let scheduled_at = null;
    if (values.send_mode === 'SCHEDULED' && values.scheduled_date && values.scheduled_time) {
      scheduled_at = `${values.scheduled_date} ${values.scheduled_time}:00`;
    }
    return {
      id: campaignId,
      name: values.name, channel: values.channel, campaign_type: values.campaign_type,
      country: Number(values.country) || 1,
      priority: values.priority, title: values.title, body: values.body,
      cta_label: values.cta_label, cta_link: values.cta_link,
      whatsapp_template: values.whatsapp_template, whatsapp_lang: values.whatsapp_lang,
      audience_type: values.audience_type, send_mode: values.send_mode,
      scheduled_at, timezone: values.timezone,
      is_live: values.is_live ? 1 : 0, test_recipients: values.test_recipients,
      retry_enabled: values.retry_enabled ? 1 : 0,
      max_retry_count: Number(values.max_retry_count) || 0,
      status: values.send_mode === 'SCHEDULED' ? 'SCHEDULED' : 'DRAFT',
    };
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = buildPayload(values);
        const res = await apicall.post(payload, 'campaigns');
        const savedId = res.data.campaign_id;

        // Upload CSV audience if a new file was chosen for a USER_LIST campaign.
        if (values.audience_type === 'USER_LIST' && csvFile) {
          await apicall.post(
            { campaign_id: savedId, file_name: csvFile.name, file: csvFile.base64 },
            'campaign_audience_file'
          );
        }

        if (action === 'send') {
          const sendRes = await apicall.post({ campaign_id: savedId }, 'campaign/send_now');
          toast.success(`Campaign executed: ${sendRes.data.sent} sent, ${sendRes.data.failed} failed, ${sendRes.data.skipped} skipped`);
        } else if (action === 'schedule') {
          await apicall.post({ campaign_id: savedId, scheduled_at: payload.scheduled_at }, 'campaign/schedule');
          toast.success('Campaign scheduled');
        } else {
          toast.success(res.message || 'Campaign saved');
        }
        handleCancel();
      } catch (error) {
        toast.error(error.message.replace('Error:', ''));
      } finally {
        setLoading(false);
      }
    },
  });

  const submitWith = (act) => {
    if (act === 'schedule' && (!formik.values.scheduled_date || !formik.values.scheduled_time)) {
      toast.error('Pick a scheduled date and time first');
      return;
    }
    setAction(act);
    formik.handleSubmit();
  };

  const inputClass = (field) =>
    formik.touched[field] && formik.errors[field] ? 'form-control is-invalid' : 'form-control';

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCsvFile({ name: file.name, base64: reader.result });
    reader.readAsDataURL(file);
  };

  const handleCancel = () => {
    navigate('/marketing-campaigns', { state: navState });
  };

  const v = formik.values;

  return (
    <div className="container bg-white p-3 mt-4">
      <h1 className="heading">
        <span className="me-4 pointer"><IoArrowBack size={24} onClick={handleCancel} style={{ cursor: 'pointer' }} /></span>
        {campaignId ? 'Edit Campaign' : 'Add Campaign'}
      </h1>
      <hr />

      <form onSubmit={formik.handleSubmit}>
        {/* 1. Campaign Details */}
        <h5 className="sub-heading mt-3">Campaign Details</h5>
        <div className="row mb-3 mt-2">
          <div className="col-md-6">
            <label className="label">Campaign Name</label>
            <input name="name" className={inputClass('name')} value={v.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
            <div className="invalid-feedback">{formik.errors.name}</div>
          </div>
          <div className="col-md-3">
            <label className="label">Channel</label>
            <select name="channel" className="form-control" value={v.channel} onChange={formik.handleChange}>
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="label">Priority</label>
            <select name="priority" className="form-control" value={v.priority} onChange={formik.handleChange}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="label">Campaign Type</label>
            <select name="campaign_type" className="form-control" value={v.campaign_type} onChange={formik.handleChange}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="label">Country</label>
            <select name="country" className="form-control" value={v.country} onChange={formik.handleChange}>
              {countries.map((c) => <option key={c.country_id} value={c.country_id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* 2. Message Content */}
        <h5 className="sub-heading mt-4">Message Content</h5>
        <div className="row mb-3 mt-2">
          <div className="col-md-6">
            <label className="label">Title {v.channel === 'PUSH' ? '(required)' : '(optional)'}</label>
            <input name="title" className={inputClass('title')} value={v.title} onChange={formik.handleChange} onBlur={formik.handleBlur} />
            {v.channel === 'PUSH' && <small className="text-muted">{v.title.length}/{PUSH_TITLE_LIMIT}</small>}
            <div className="invalid-feedback">{formik.errors.title}</div>
          </div>
          {v.channel === 'WHATSAPP' && (
            <>
              <div className="col-md-3">
                <label className="label">WhatsApp Template</label>
                <input name="whatsapp_template" className={inputClass('whatsapp_template')} value={v.whatsapp_template} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                <div className="invalid-feedback">{formik.errors.whatsapp_template}</div>
              </div>
              <div className="col-md-3">
                <label className="label">Template Language</label>
                <input name="whatsapp_lang" className="form-control" value={v.whatsapp_lang} onChange={formik.handleChange} />
              </div>
            </>
          )}
        </div>
        <div className="row mb-3">
          <div className="col-md-12">
            <label className="label">Message Body</label>
            <textarea name="body" rows="4" className={inputClass('body')} value={v.body} onChange={formik.handleChange} onBlur={formik.handleBlur} />
            {v.channel === 'SMS' && (
              <small className={v.body.length > SMS_WARN_LIMIT ? 'text-danger' : 'text-muted'}>
                {v.body.length} chars {v.body.length > SMS_WARN_LIMIT ? `(over ${SMS_WARN_LIMIT}, multiple SMS)` : ''}
              </small>
            )}
            {v.channel === 'PUSH' && <small className={v.body.length > PUSH_BODY_LIMIT ? 'text-danger' : 'text-muted'}>{v.body.length}/{PUSH_BODY_LIMIT}</small>}
            {v.channel === 'WHATSAPP' && <small className="text-muted">Body is passed as the template's first parameter. Ensure your approved template matches.</small>}
            <div className="invalid-feedback">{formik.errors.body}</div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="label">CTA Label (optional)</label>
            <input name="cta_label" className="form-control" value={v.cta_label} onChange={formik.handleChange} />
          </div>
          <div className="col-md-6">
            <label className="label">CTA Deep Link / App Route (optional)</label>
            <input name="cta_link" className="form-control" value={v.cta_link} onChange={formik.handleChange} />
          </div>
        </div>

        {/* 3. Audience Selection */}
        <h5 className="sub-heading mt-4">Audience Selection</h5>
        <div className="row mb-3 mt-2">
          <div className="col-md-6">
            <label className="label">Audience</label>
            <select name="audience_type" className="form-control" value={v.audience_type} onChange={formik.handleChange}>
              <option value="ALL_CUSTOMERS">All Customers</option>
              <option value="USER_LIST">Specific User List (CSV)</option>
            </select>
          </div>
          {v.audience_type === 'USER_LIST' && (
            <div className="col-md-6">
              <label className="label">Upload CSV (customer_id, phone, push_token)</label>
              <input type="file" accept=".csv" className="form-control" onChange={handleCsvChange} />
              {csvFile && <small className="primaryColorText">{csvFile.name} selected</small>}
            </div>
          )}
        </div>

        {/* 4. Delivery Settings */}
        <h5 className="sub-heading mt-4">Delivery Settings</h5>
        <div className="row mb-3 mt-2">
          <div className="col-md-3">
            <label className="label">Send Mode</label>
            <select name="send_mode" className="form-control" value={v.send_mode} onChange={formik.handleChange}>
              <option value="NOW">Send Now</option>
              <option value="SCHEDULED">Schedule</option>
            </select>
          </div>
          {v.send_mode === 'SCHEDULED' && (
            <>
              <div className="col-md-3">
                <label className="label">Scheduled Date</label>
                <input type="date" name="scheduled_date" className="form-control" value={v.scheduled_date} onChange={formik.handleChange} />
              </div>
              <div className="col-md-3">
                <label className="label">Scheduled Time</label>
                <select name="scheduled_time" className="form-control" value={v.scheduled_time} onChange={formik.handleChange}>
                  <option value="">— select time —</option>
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="label">Time Zone</label>
                <select name="timezone" className="form-control" value={v.timezone} onChange={formik.handleChange}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="label d-block">Retry on Failure</label>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" name="retry_enabled" checked={v.retry_enabled} onChange={formik.handleChange} />
              <span className="ms-1">{v.retry_enabled ? 'Yes' : 'No'}</span>
            </div>
          </div>
          {v.retry_enabled && (
            <div className="col-md-3">
              <label className="label">Max Retry Count</label>
              <input type="number" name="max_retry_count" min="0" className="form-control" value={v.max_retry_count} onChange={formik.handleChange} />
            </div>
          )}
        </div>

        {/* 5. Test / Live mode */}
        <h5 className="sub-heading mt-4">Send Mode (Safety)</h5>
        <div className="row mb-3 mt-2 LightbackgroundColour p-2 rounded">
          <div className="col-md-4">
            <label className="label d-block">Live broadcast</label>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" name="is_live" checked={v.is_live} onChange={formik.handleChange} />
              <span className="ms-1">{v.is_live ? 'LIVE — real audience' : 'TEST — allowlist only'}</span>
            </div>
          </div>
          {!v.is_live && (
            <div className="col-md-8">
              <label className="label">
                Test Recipients {v.channel === 'PUSH' ? '(comma/newline separated User IDs)' : '(comma/newline separated phone numbers)'}
              </label>
              <textarea name="test_recipients" rows="2" className={inputClass('test_recipients')} value={v.test_recipients} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              <div className="invalid-feedback">{formik.errors.test_recipients}</div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-center mt-5 mb-4 gap-2 flex-wrap">
          <button type="button" className="btn Button primaryColorText btn-outline-secondary no-hover-effect" onClick={handleCancel}>
            Cancel
          </button>
          {v.send_mode === 'SCHEDULED' && (
            <button type="button" className="btn Button primaryBackground text-white" style={{ fontSize: '12px', lineHeight: '1.3', textAlign: 'center' }} disabled={loading} onClick={() => submitWith('schedule')}>
              {loading && action === 'schedule' ? <Loader style={{ height: '30', width: '40' }} /> : 'Schedule Campaign'}
            </button>
          )}
          {v.send_mode === 'NOW' && (
            <button type="button" className="btn Button primaryBackground text-white" style={{ fontSize: '12px', lineHeight: '1.3', textAlign: 'center' }} disabled={loading} onClick={() => submitWith('send')}>
              {loading && action === 'send' ? <Loader style={{ height: '30', width: '40' }} /> : 'Save & Send Now'}
            </button>
          )}
        </div>
      </form>
      <ToastContainer />
    </div>
  );
};

export default MarketingCampaignForm;
